import styles from "./MessageCoachPanel.module.scss";

import { ChangeEvent, useCallback, useMemo } from "react";

import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/modules/shared/components/AppButton";
import { AppInput } from "@/modules/shared/components/AppInput";
import { IconButton } from "@/modules/shared/components/IconButton";

import { BookingKinds, useBookingContext } from "../../context/BookingContext";
import ArrowLeft from "../../svgs/arrow-left.svg";
import { CalendarIcon } from "../../svgs/CalendarIcon";
import { NeedHelpLink } from "../NeedHelpLink";

const MAX_NOTES_LENGTH = 500;

type MessageCoachPanelProps = {
  supportRoute?: string;
};

export function MessageCoachPanel({ supportRoute }: MessageCoachPanelProps) {
  const { t } = useTranslation();

  const {
    selectedDateTime,
    timezone,
    clearSelectedDateTime,
    preparationNotes,
    setPreparationNotes,
    setNotesStepDone,
    bookingKind,
  } = useBookingContext();

  const copyKey =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE
      ? "book.messageCoach.coachMatching"
      : "book.messageCoach";

  const dateTimeLabel = useMemo(() => {
    if (!selectedDateTime || !timezone) {
      return "";
    }

    const date = new Date(selectedDateTime);
    const datePart = formatInTimeZone(date, timezone.value, "EEEE, MMMM d");
    const timePart = formatInTimeZone(
      date,
      timezone.value,
      "h:mma",
    ).toLowerCase();

    return `${datePart} at ${timePart}`;
  }, [selectedDateTime, timezone]);

  const handleNotesChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPreparationNotes(e.target.value);
    },
    [setPreparationNotes],
  );

  const handleContinue = useCallback(() => {
    setNotesStepDone(true);
  }, [setNotesStepDone]);

  return (
    <div className={styles.container}>
      <div className={styles.headerBlock}>
        <div className={styles.header}>
          <IconButton
            icon={ArrowLeft}
            aria-label={t("book.messageCoach.back")}
            className={styles.arrowIconContainer}
            onClick={clearSelectedDateTime}
          />
          <span className={styles.title}>{t(`${copyKey}.title`)}</span>
        </div>
        <div className={styles.dateTimeRow}>
          <CalendarIcon className={styles.calendarIcon} />
          <span className={styles.dateTime}>{dateTimeLabel}</span>
        </div>
      </div>
      <span className={styles.body}>{t(`${copyKey}.body`)}</span>
      <AppInput
        type="textarea"
        className={styles.appInput}
        label={t(`${copyKey}.label`)}
        placeholder={t("book.messageCoach.placeholder")}
        value={preparationNotes}
        onChange={handleNotesChange}
        maxLength={MAX_NOTES_LENGTH}
        showCharacterCounter
        autoGrow
        characterCounterFormat={(count, max) => `${count} / ${max}`}
      />
      <div className={styles.needHelpContainer}>
        <NeedHelpLink mobileLink to={supportRoute} />
      </div>
      <AppButton
        variant="critical"
        className={styles.cta}
        onClick={handleContinue}
      >
        {t("book.messageCoach.cta")}
      </AppButton>
    </div>
  );
}
